import { useState, useEffect } from "react";
import API from "../lib/api";
import { MapPin, Clock, Loader2, RefreshCw, Smartphone } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const childIcon = new L.DivIcon({
  html: `<div style="width:40px;height:40px;border-radius:50%;background:#FF5C39;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.2);display:flex;align-items:center;justify-content:center;">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  className: "",
});

function formatTimestamp(ts) {
  if (!ts) return "Unknown";
  const d = new Date(ts);
  const now = new Date();
  const diff = (now - d) / 1000 / 60;
  if (diff < 1) return "Just now";
  if (diff < 60) return `${Math.round(diff)} min ago`;
  if (diff < 1440) return `${Math.round(diff / 60)} hours ago`;
  return d.toLocaleDateString();
}

export default function LocationPage() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchLocation();
  }, []);

  const fetchLocation = async () => {
    try {
      const [locRes, histRes] = await Promise.all([API.get("/location"), API.get("/location/history")]);
      setCurrent(locRes.data);
      setHistory(histRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await fetchLocation();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#FF5C39] animate-spin" />
      </div>
    );
  }

  const noLocation = !current?.lat || current?.no_device;

  return (
    <div className="space-y-6" data-testid="location-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#1F1F2E] tracking-tight">Location</h1>
          <p className="text-sm font-semibold text-[#52526B] mt-1">See where your child is right now</p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="refresh-location-button"
          className="bg-white border-2 border-[#F0E4D8] rounded-2xl p-3 hover:border-[#4D96FF] transition-all active:scale-95"
        >
          <RefreshCw className={`w-5 h-5 text-[#52526B] ${refreshing ? "animate-spin" : ""}`} strokeWidth={2.5} />
        </button>
      </div>

      {noLocation ? (
        /* No location data / no device */
        <div className="bg-white rounded-[32px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50 text-center" data-testid="no-location-card">
          <div className="w-20 h-20 bg-[#FFD23F]/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-10 h-10 text-[#FFD23F]" strokeWidth={2} />
          </div>
          <h3 className="text-xl font-extrabold text-[#1F1F2E] mb-2">No location data yet</h3>
          <p className="text-sm text-[#52526B] font-semibold max-w-sm mx-auto">
            Once your child's Alfie phone is set up and connected, their location will appear here on the map in real time.
          </p>
        </div>
      ) : (
        /* Map with location */
        <div className="bg-white rounded-[32px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="map-card">
          <div className="h-[300px] md:h-[400px] relative">
            <MapContainer
              center={[current.lat, current.lng]}
              zoom={15}
              style={{ height: "100%", width: "100%" }}
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[current.lat, current.lng]} icon={childIcon}>
                <Popup>
                  <div className="font-nunito">
                    <p className="font-bold text-sm">{current.address}</p>
                    <p className="text-xs text-gray-500">{formatTimestamp(current.timestamp)}</p>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </div>
          <div className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#FF5C39]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#FF5C39]" strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-bold text-[#1F1F2E]" data-testid="current-address">{current?.address || "Unknown location"}</p>
                <p className="text-xs font-semibold text-[#52526B] flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  Updated {formatTimestamp(current?.timestamp)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location History */}
      {history.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#52526B] mb-3">Recent places (last 24 hours)</h3>
          <div className="space-y-0.5">
            {history.map((loc, i) => (
              <div key={i} className="flex items-start gap-4" data-testid={`location-history-${i}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full ${i === 0 ? "bg-[#FF5C39]" : "bg-[#F0E4D8]"}`} />
                  {i < history.length - 1 && <div className="w-0.5 h-12 bg-[#F0E4D8]" />}
                </div>
                <div className="pb-4">
                  <p className="font-bold text-sm text-[#1F1F2E]">{loc.address}</p>
                  <p className="text-xs font-semibold text-[#52526B]">{formatTimestamp(loc.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
